import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

interface User {
  _id: string;
  firebaseUid: string;
  email: string;
  roles: string[];
  isActive: boolean;
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if current user is master or admin
  const isMasterOrAdmin = user?.roles?.some(role => ["master", "admin"].includes(role));

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get("/users");
        setUsers(response.data);
      } catch (err) {
        setError("Failed to fetch users");
        console.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };

    if (isMasterOrAdmin) {
      fetchUsers();
    }
  }, [isMasterOrAdmin]);

  const handleActivationToggle = async (uid: string, currentStatus: boolean) => {
    try {
      const response = await api.patch(`/users/${uid}/activation`, {
        isActive: !currentStatus,
      });
      
      // Update local state
      setUsers(users.map(u => 
        u.firebaseUid === uid ? response.data : u
      ));
    } catch (err) {
      setError("Failed to update user activation status");
      console.error("Error updating user activation:", err);
    }
  };

  const handleRoleUpdate = async (uid: string, roles: string[]) => {
    try {
      const response = await api.patch(`/users/${uid}/roles`, { roles });
      
      // Update local state
      setUsers(users.map(u => 
        u.firebaseUid === uid ? response.data : u
      ));
    } catch (err) {
      setError("Failed to update user roles");
      console.error("Error updating user roles:", err);
    }
  };

  if (!isMasterOrAdmin) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">Users</h1>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">Users</h1>
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Users Management</h1>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-6 py-3 border-b text-left">Email</th>
              <th className="px-6 py-3 border-b text-left">Roles</th>
              <th className="px-6 py-3 border-b text-center">Status</th>
              <th className="px-6 py-3 border-b text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 border-b">{user.email}</td>
                <td className="px-6 py-4 border-b">
                  <div className="flex flex-wrap gap-1">
                    {user.roles.map((role) => (
                      <span
                        key={role}
                        className={`px-2 py-1 rounded text-sm ${
                          role === "master"
                            ? "bg-red-100 text-red-800"
                            : role === "admin"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 border-b text-center">
                  <span
                    className={`px-2 py-1 rounded ${
                      user.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {user.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-6 py-4 border-b text-center">
                  {/* Only master can modify master/admin users */}
                  {(!user.roles.some(role => ["master", "admin"].includes(role)) || 
                    user?.roles?.includes("master")) && (
                    <button
                      onClick={() => handleActivationToggle(user.firebaseUid, user.isActive)}
                      className={`px-4 py-2 rounded ${
                        user.isActive
                          ? "bg-red-500 hover:bg-red-600 text-white"
                          : "bg-green-500 hover:bg-green-600 text-white"
                      }`}
                    >
                      {user.isActive ? "Deactivate" : "Activate"}
                    </button>
                  )}
                  
                  {/* Only master can modify roles */}
                  {user?.roles?.includes("master") && (
                    <div className="mt-2">
                      <select
                        className="border rounded px-2 py-1"
                        value=""
                        onChange={(e) => {
                          const newRoles = [...user.roles];
                          if (!newRoles.includes(e.target.value)) {
                            newRoles.push(e.target.value);
                            handleRoleUpdate(user.firebaseUid, newRoles);
                          }
                        }}
                      >
                        <option value="">Add Role</option>
                        {["master", "admin", "user"].map(role => (
                          !user.roles.includes(role) && (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          )
                        ))}
                      </select>
                      {user.roles.length > 1 && (
                        <div className="mt-2">
                          <select
                            className="border rounded px-2 py-1"
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                const newRoles = user.roles.filter(
                                  r => r !== e.target.value && r !== "user"
                                );
                                // Ensure 'user' role is always present
                                if (!newRoles.includes("user")) {
                                  newRoles.push("user");
                                }
                                handleRoleUpdate(user.firebaseUid, newRoles);
                              }
                            }}
                          >
                            <option value="">Remove Role</option>
                            {user.roles.map(role => (
                              role !== "user" && (
                                <option key={role} value={role}>
                                  {role}
                                </option>
                              )
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
